import { motion } from 'framer-motion';
import { X, Info, AlertCircle, AlertTriangle, TrendingUp, Store } from 'lucide-react';
import { getNeumorphicShadow, getNeumorphicHover } from '@/lib/designSystem';
import { useTheme } from '@/contexts/ThemeContext';

const InsightDetailModal = ({ insight, onClose, onStatusUpdate, currentColors }) => {
  const { theme } = useTheme();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="insight-detail-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        style={{ background: currentColors.cardBg, boxShadow: getNeumorphicHover(theme) }}
      >
        {/* Header */}
        <div className="p-5 border-b" style={{ borderColor: currentColors.border }}>
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold mb-1" style={{ color: currentColors.textPrimary }}>
                {insight.title}
              </h2>
              <p className="text-sm" style={{ color: currentColors.textSecondary }}>
                {insight.description}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: currentColors.background }}
              data-testid="insight-modal-close-btn"
            >
              <X size={18} style={{ color: currentColors.textSecondary }} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5">
          {/* PRICE_VARIANCE */}
          {insight.type === 'PRICE_VARIANCE' && insight.payload?.outlet_prices && (
            <div className="space-y-4">
              <div className="rounded-xl p-4" style={{ background: currentColors.accentBlue + '20', border: `1px solid ${currentColors.accentBlue}` }}>
                <h3 className="font-semibold text-sm mb-1 flex items-center gap-2" style={{ color: currentColors.textPrimary }}>
                  <Info size={16} style={{ color: currentColors.accentBlue }} /> Interpretation
                </h3>
                <p className="text-xs" style={{ color: currentColors.textSecondary }}>
                  The price of <strong>{insight.payload.ingredient_name}</strong> varies significantly across outlets.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {insight.payload.outlet_prices.map((op, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl p-4"
                    style={{ background: currentColors.cardBg, border: `1px solid ${currentColors.border}`, boxShadow: getNeumorphicShadow(theme) }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm" style={{ color: currentColors.textPrimary }}>
                        {op.outlet_name}
                      </span>
                      <Store size={16} style={{ color: currentColors.accentBlue }} />
                    </div>
                    <div className="text-2xl font-bold" style={{ color: currentColors.textPrimary }}>
                      ₹{op.avg_price ? op.avg_price.toFixed(2) : op.price?.toFixed(2)}
                    </div>
                    <div className="mt-1 text-xs" style={{ color: currentColors.textSecondary }}>average price</div>
                  </div>
                ))}
              </div>
              <div className="rounded-xl p-3" style={{ background: currentColors.accentOrange + '20', border: `1px solid ${currentColors.accentOrange}` }}>
                <div className="flex items-center gap-2">
                  <AlertTriangle size={16} style={{ color: currentColors.accentOrange }} />
                  <span className="font-semibold text-sm" style={{ color: currentColors.textPrimary }}>Variance: </span>
                  <span className="text-xl font-bold" style={{ color: currentColors.accentOrange }}>
                    {insight.payload.variance_percent}%
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* DUPLICATE_BILL */}
          {insight.type === 'DUPLICATE_BILL' && (
            <div className="space-y-4">
              <div className="rounded-xl p-4" style={{ background: currentColors.accentRed + '20', border: `1px solid ${currentColors.accentRed}` }}>
                <h3 className="font-semibold text-sm flex items-center gap-2" style={{ color: currentColors.textPrimary }}>
                  <AlertCircle size={16} style={{ color: currentColors.accentRed }} /> Potential duplicate bill detected
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl p-4" style={{ border: `2px solid ${currentColors.accentRed}`, background: currentColors.cardBg }}>
                  <p className="text-xs mb-1" style={{ color: currentColors.textSecondary }}>Bill</p>
                  <p className="font-semibold" style={{ color: currentColors.textPrimary }}>{insight.payload.bill_number}</p>
                  <p className="text-xs" style={{ color: currentColors.textSecondary }}>{insight.payload.bill_date}</p>
                  <p className="text-xl font-bold mt-1" style={{ color: currentColors.accentRed }}>
                    ₹{insight.payload.bill_total?.toLocaleString()}
                  </p>
                </div>
                <div className="rounded-xl p-4" style={{ border: `2px solid ${currentColors.accentYellow}`, background: currentColors.cardBg }}>
                  <p className="text-xs mb-1" style={{ color: currentColors.textSecondary }}>Match</p>
                  <p className="font-semibold" style={{ color: currentColors.textPrimary }}>{insight.payload.bill_number}</p>
                  <p className="text-xs" style={{ color: currentColors.textSecondary }}>{insight.payload.bill_date}</p>
                  <p className="text-xl font-bold mt-1" style={{ color: currentColors.accentOrange }}>
                    ₹{insight.payload.bill_total?.toLocaleString()}
                  </p>
                </div>
              </div>
              {insight.payload.matched_fields && (
                <div className="flex flex-wrap gap-2">
                  {insight.payload.matched_fields.map((f, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 text-xs rounded-full"
                      style={{ background: currentColors.accentRed + '20', color: currentColors.accentRed }}
                    >
                      {f.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* VENDOR_RISK_ALERT */}
          {insight.type === 'VENDOR_RISK_ALERT' && (
            <div className="space-y-4">
              <div
                className="rounded-xl p-5 text-white"
                style={{ background: `linear-gradient(135deg, ${currentColors.accentOrange}, ${currentColors.accentRed})` }}
              >
                <p className="text-xs opacity-90">Overdue Amount</p>
                <p className="text-3xl font-bold">₹{insight.payload.overdue_amount?.toLocaleString()}</p>
                <p className="text-sm mt-1">{insight.payload.days_overdue} days overdue</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl p-3" style={{ background: currentColors.cardBg, border: `1px solid ${currentColors.border}` }}>
                  <p className="text-xs" style={{ color: currentColors.textSecondary }}>Credit Limit</p>
                  <p className="text-xl font-bold" style={{ color: currentColors.textPrimary }}>
                    ₹{insight.payload.credit_limit?.toLocaleString()}
                  </p>
                </div>
                <div className="rounded-xl p-3" style={{ background: currentColors.accentRed + '20', border: `1px solid ${currentColors.accentRed}` }}>
                  <p className="text-xs" style={{ color: currentColors.textSecondary }}>Utilization</p>
                  <p className="text-xl font-bold" style={{ color: currentColors.accentRed }}>
                    {insight.payload.utilization_percent}%
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* BENCHMARK_INSIGHT */}
          {insight.type === 'BENCHMARK_INSIGHT' && (
            <div className="space-y-4">
              <div
                className="rounded-xl p-5 text-white text-center"
                style={{ background: `linear-gradient(135deg, ${currentColors.accentGreen}, ${currentColors.accentBlue})` }}
              >
                <p className="text-xs opacity-90">Total Spending</p>
                <p className="text-3xl font-bold">₹{insight.payload.total_spending?.toLocaleString()}</p>
                <p className="text-sm">{insight.payload.total_bills} bills</p>
              </div>
              {insight.payload.top_vendor_name && (
                <div className="rounded-xl p-4" style={{ background: currentColors.cardBg, border: `1px solid ${currentColors.border}` }}>
                  <p className="text-sm font-medium mb-2" style={{ color: currentColors.textPrimary }}>
                    Top Vendor: {insight.payload.top_vendor_name}
                  </p>
                  <div className="h-4 rounded-full overflow-hidden" style={{ background: currentColors.background }}>
                    <div
                      className="h-full rounded-full text-xs text-white flex items-center justify-center"
                      style={{ width: `${insight.payload.top_vendor_percentage}%`, background: currentColors.accentBlue }}
                    >
                      {insight.payload.top_vendor_percentage?.toFixed(1)}%
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Generic fallback */}
          {!['PRICE_VARIANCE', 'DUPLICATE_BILL', 'VENDOR_RISK_ALERT', 'BENCHMARK_INSIGHT'].includes(insight.type) && (
            <div className="rounded-xl p-4" style={{ background: currentColors.background }}>
              <h4 className="font-medium text-sm mb-2" style={{ color: currentColors.textPrimary }}>Details</h4>
              <pre className="text-xs whitespace-pre-wrap" style={{ color: currentColors.textSecondary }}>
                {JSON.stringify(insight.payload, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {insight.status === 'NEW' && (
          <div className="p-5 border-t flex gap-3" style={{ borderColor: currentColors.border }}>
            <button
              onClick={() => onStatusUpdate(insight.insight_id, 'ACKED')}
              className="flex-1 px-4 py-2.5 rounded-xl font-medium text-sm text-white"
              style={{ background: currentColors.accentYellow }}
              data-testid="insight-ack-btn"
            >
              Acknowledge
            </button>
            <button
              onClick={() => onStatusUpdate(insight.insight_id, 'RESOLVED')}
              className="flex-1 px-4 py-2.5 rounded-xl font-medium text-sm text-white"
              style={{ background: currentColors.accentGreen }}
              data-testid="insight-resolve-btn"
            >
              Resolve
            </button>
            <button
              onClick={() => onStatusUpdate(insight.insight_id, 'DISMISSED')}
              className="flex-1 px-4 py-2.5 rounded-xl font-medium text-sm"
              style={{ background: currentColors.background, color: currentColors.textSecondary }}
              data-testid="insight-dismiss-btn"
            >
              Dismiss
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default InsightDetailModal;
